import { assignMedalTiers, type MedalTier } from "@dailywars/core/medals";
import { Link } from "@tanstack/react-router";
import { Video } from "lucide-react";
import { useMemo } from "react";

import { MedalIcon } from "#/components/MedalIcon";
import { PlayerPreviewName } from "#/components/PlayerPreviewName";
import { Button } from "#/components/ui/button";
import { Separator } from "#/components/ui/separator";
import { cn } from "#/lib/utils";

import { HERO_LEADERBOARD_TOP_COUNT, type LeaderboardEntry } from "./-play.labels";

export function MapDayLeaderboard({
  entries,
  mapId,
  medalsVisible,
  showReplays = false,
  title = "Leaderboard",
}: {
  entries: LeaderboardEntry[];
  mapId: string;
  medalsVisible: boolean;
  showReplays?: boolean;
  title?: string;
}) {
  return (
    <div className="flex h-full w-full flex-col gap-3 rounded-lg bg-background p-3 text-left ring-1 ring-foreground/10">
      <p className="text-center text-xs font-semibold tracking-wide text-muted-foreground uppercase">
        {title}
      </p>
      <MapLeaderboardEntryList
        entries={entries}
        medalsVisible={medalsVisible}
        showReplays={showReplays}
      />
      <Separator />
      <Button
        size="sm"
        variant="link"
        className="mt-auto"
        render={<Link to="/leaderboard/$mapId" params={{ mapId }} preload={false} />}
        nativeButton={false}
      >
        Full leaderboard →
      </Button>
    </div>
  );
}

function MapLeaderboardEntryLabel({ entry }: { entry: LeaderboardEntry | null }) {
  if (!entry) {
    return <span className="text-muted-foreground">No score yet</span>;
  }
  return <PlayerPreviewName playerId={entry.playerId} displayName={entry.displayName} />;
}

function MapLeaderboardReplayLink({ replayId }: { replayId: string }) {
  return (
    <Button
      variant="accent"
      size="icon-sm"
      render={<Link to="/replay/$replayId" params={{ replayId }} preload={false} rel="nofollow" />}
      nativeButton={false}
      aria-label="Watch replay"
    >
      <Video />
    </Button>
  );
}

function MapLeaderboardEntryRow({
  rank,
  entry,
  medal,
  medalsVisible,
  showReplays,
}: {
  rank: number;
  entry: LeaderboardEntry | null;
  medal: MedalTier | undefined;
  medalsVisible: boolean;
  showReplays: boolean;
}) {
  return (
    <li className="flex items-center justify-between gap-3 border-b border-border/60 pb-1.5 last:border-0">
      <span className="flex min-w-0 flex-1 items-center gap-1.5 truncate text-foreground">
        {medalsVisible ? (
          <span className="inline-flex w-4 shrink-0 justify-center">
            {medal ? <MedalIcon tier={medal} size={14} /> : null}
          </span>
        ) : null}
        <span className="min-w-0 truncate">
          <span className="text-muted-foreground">{rank}. </span>
          <MapLeaderboardEntryLabel entry={entry} />
        </span>
      </span>
      <span className="flex shrink-0 items-center gap-1.5">
        <span className="tabular-nums text-muted-foreground">
          {entry ? (
            <>
              {entry.score} {entry.score === 1 ? "day" : "days"}
            </>
          ) : null}
        </span>
        {showReplays && entry?.replayId ? (
          <MapLeaderboardReplayLink replayId={entry.replayId} />
        ) : null}
      </span>
    </li>
  );
}

function MapLeaderboardEntryList({
  entries,
  medalsVisible,
  showReplays,
}: {
  entries: LeaderboardEntry[];
  medalsVisible: boolean;
  showReplays: boolean;
}) {
  const medalsByPlayerId = useMemo(() => assignMedalTiers(entries), [entries]);
  const rows = Array.from({ length: HERO_LEADERBOARD_TOP_COUNT }, (_, index) => ({
    rank: index + 1,
    entry: entries[index] ?? null,
  }));

  return (
    <ol
      className={cn("mx-auto space-y-1.5 text-xs", showReplays ? "max-w-[280px]" : "max-w-[220px]")}
    >
      {rows.map(({ rank, entry }) => (
        <MapLeaderboardEntryRow
          key={entry ? `${rank}-${entry.score}-${entry.displayName}` : `empty-${rank}`}
          rank={rank}
          entry={entry}
          medal={entry ? medalsByPlayerId.get(entry.playerId) : undefined}
          medalsVisible={medalsVisible}
          showReplays={showReplays}
        />
      ))}
    </ol>
  );
}
